import { ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { AchMethodModel, AchVerifyResult } from '../../../customer/customer.model';

interface AchVerifyForm {
  account: FormControl<string | null>;
}

/**
 * Confirms the account number behind a redacted ACH row.
 *
 * The entered number is deliberately never echoed back, never pre-filled and
 * never revealed: this form only asks "is this the account?" and shows a
 * yes/no answer. The number is encrypted before leaving the browser by
 * SecureClientService, so it is not visible in network traffic either.
 */
@Component({
  selector: 'app-ach-verify-account-form',
  standalone: true,
  imports: [
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    MatIconModule,
    ReactiveFormsModule,
    NgIf,
  ],
  templateUrl: './ach-verify-account-form.component.html',
  host: {
    class: 'app-ach-verify-account-form',
  },
})
export class AchVerifyAccountFormComponent implements OnInit {
  @Input() data: { payment: AchMethodModel | null } = { payment: null };

  // The result is set by the modal's Verify button handler, which lives outside
  // this component's own event bindings, so the view has to be marked explicitly
  // or the status line would not appear until the next unrelated interaction.
  private cdr = inject(ChangeDetectorRef);

  /** The redacted account shown as read-only context, e.g. XXXXXXXX9012. */
  redactedAccount = '';

  /**
   * False for accounts stored before the number was kept encrypted, where the
   * server can only compare the last 4 digits. Surfaced up front so a pass is
   * not over-trusted.
   */
  accountVerifiable = true;

  /**
   * A failed verification. A success closes the dialog, so only mismatches are
   * ever rendered here.
   */
  failure: AchVerifyResult | null = null;
  errorMessage = '';
  isVerifying = false;

  form = new FormGroup<AchVerifyForm>({
    account: new FormControl<string | null>(null, [
      Validators.required,
      Validators.minLength(5),
      Validators.maxLength(17),
      Validators.pattern(/^\d{5,17}$/),
    ]),
  });

  ngOnInit(): void {
    this.redactedAccount = this.data?.payment?.account ?? '';
    this.accountVerifiable = this.data?.payment?.accountVerifiable !== false;

    // Editing the number clears the previous answer so a stale mismatch is not
    // read against a number the user has since corrected.
    this.form.controls.account.valueChanges.subscribe(() => {
      this.failure = null;
      this.errorMessage = '';
    });
  }

  get accountNumber(): string {
    return (this.form.controls.account.value ?? '').trim();
  }

  get statusMessage(): string {
    if (this.errorMessage) {
      return this.errorMessage;
    }
    if (!this.failure) {
      return '';
    }
    const remaining = this.failure.attemptsRemaining;
    const suffix = typeof remaining === 'number' && remaining > 0
      ? ` ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`
      : '';
    return `${this.failure.message || 'That account number does not match the account on file.'}${suffix}`;
  }

  setResult(result: AchVerifyResult) {
    this.errorMessage = '';
    this.failure = result.verified ? null : result;
    this.cdr.markForCheck();
  }

  setError(message: string) {
    this.failure = null;
    this.errorMessage = message;
    this.cdr.markForCheck();
  }
}
